import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch";

export default function useGrowthLoop() {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();

    const [loading, setLoading] = useState(false);
    const [assessment, setAssessment] = useState(null);

    // =========================
    // Assessment APIs
    // =========================

    const loadAssessment = useCallback(
        async (id) => {
            setLoading(true);

            try {
                const res = await fetchData(
                    {
                        url: `/gl/${id}`,
                        method: "GET",
                        headers: {
                            Authorization: authState.token,
                        },
                    },
                    { silent: true }
                );

                if (res?.success) {
                    setAssessment(res.data);
                }

                return res;
            } catch (err) {
                console.error("loadAssessment", err);
                return { success: false };
            } finally {
                setLoading(false);
            }
        },
        [fetchData, authState.token]
    );

    const loadMyAssessments = useCallback(
        async () => {
            setLoading(true);

            try {
                return await fetchData(
                    {
                        url: "/gl/my",
                        method: "GET",
                        headers: {
                            Authorization: authState.token,
                        },
                    },
                    { silent: true }
                );
            } catch (err) {
                console.error("loadMyAssessments", err);

                return {
                    success: false,
                    data: [],
                };
            } finally {
                setLoading(false);
            }
        },
        [fetchData, authState.token]
    );

    const saveAssessment = useCallback(
        async (payload) => {
            setLoading(true);

            try {
                const res = await fetchData(
                    {
                        url: "/gl",
                        method: "POST",
                        data: payload,
                        headers: {
                            Authorization: authState.token,
                        },
                    },
                    { silent: true }
                );

                if (res?.success) {
                    setAssessment(res.data);
                }

                return res;
            } catch (err) {
                console.error("saveAssessment", err);
                return { success: false };
            } finally {
                setLoading(false);
            }
        },
        [fetchData, authState.token]
    );

    const updateAssessment = useCallback(
        async (id, payload) => {
            setLoading(true);

            try {
                const res = await fetchData(
                    {
                        url: `/gl/${id}`,
                        method: "PUT",
                        data: payload,
                        headers: {
                            Authorization: authState.token,
                        },
                    },
                    { silent: true }
                );

                if (res?.success) {
                    setAssessment(res.data);
                }

                return res;
            } catch (err) {
                console.error("updateAssessment", err);
                return { success: false };
            } finally {
                setLoading(false);
            }
        },
        [fetchData, authState.token]
    );

    // =========================
    // Child APIs
    // =========================

    const loadMyChildren = useCallback(
        async () => {
            try {
                return await fetchData(
                    {
                        url: "/gl/child/my",
                        method: "GET",
                        headers: {
                            Authorization: authState.token,
                        },
                    },
                    { silent: true }
                );
            } catch (err) {
                console.error("loadMyChildren", err);

                return {
                    success: false,
                    data: [],
                };
            }
        },
        [fetchData, authState.token]
    );

    const saveChild = useCallback(
        async (payload) => {
            try {
                return await fetchData(
                    {
                        url: "/gl/child",
                        method: "POST",
                        data: payload,
                        headers: {
                            Authorization: authState.token,
                        },
                    },
                    { silent: true }
                );
            } catch (err) {
                console.error("saveChild", err);

                return {
                    success: false,
                };
            }
        },
        [fetchData, authState.token]
    );

    // NOTE: this hook previously had its own internal useEffect that
    // silently auto-loaded the user's most recent assessment on mount.
    // That raced against GrowthLoopHome's own mount effect (which loads
    // the children list and sets `child` from it) — both effects fired
    // independently and non-deterministically decided what `child` state
    // ended up being. Removed: GrowthLoopHome.jsx's own effect + the
    // explicit History "onOpen" call are now the only ways an assessment
    // gets loaded, so there's exactly one source of truth for `child`.

    return {
        loading,

        assessment,
        setAssessment,

        loadAssessment,
        loadMyAssessments,
        saveAssessment,
        updateAssessment,

        loadMyChildren,
        saveChild,
    };
}